import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const cardVariants = cva('bg-card text-card-foreground transition-all duration-200', {
  variants: {
    variant: {
      default: 'border border-border rounded-2xl',
      outline: 'border border-border rounded-2xl bg-transparent',
      elevated: 'border border-border rounded-2xl shadow-lg',
      interactive:
        'border border-border rounded-2xl hover:border-primary/50 hover:shadow-glow cursor-pointer',
      accent: 'border border-border rounded-2xl border-l-4 border-l-accent',
      ghost: 'rounded-2xl bg-transparent',
      filled: 'rounded-2xl bg-primary/5 border border-primary/10',
    },
    padding: {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    },
  },
  defaultVariants: {
    variant: 'default',
    padding: 'md',
  },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(cardVariants({ variant, padding, className }))}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// Card Header
interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, action, children, ...props }, ref) => {
    // If using children directly (custom layout), use simpler styling
    if (children) {
      return (
        <div
          ref={ref}
          className={cn('flex flex-col space-y-1.5 pb-4', className)}
          {...props}
        >
          {children}
        </div>
      );
    }

    // Otherwise use the structured layout with title/description/action props
    return (
      <div
        ref={ref}
        className={cn('flex items-start justify-between gap-4 mb-6', className)}
        {...props}
      >
        <div className="space-y-1.5">
          {title && (
            <h3 className="text-lg font-semibold leading-none tracking-tight">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// Card Content
const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('', className)} {...props} />;
  }
);

CardContent.displayName = 'CardContent';

// Card Footer
const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-4 mt-6 pt-6 border-t border-border', className)}
        {...props}
      />
    );
  }
);

CardFooter.displayName = 'CardFooter';

// Stat Card for dashboard KPIs - redesigned with sharper look
interface StatCardProps extends Omit<CardProps, 'children'> {
  title: string;
  value: string | number;
  change?: {
    value: number;
    trend: 'up' | 'down' | 'neutral';
  };
  icon?: ReactNode;
  description?: string;
}

const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ title, value, change, icon, description, className, ...props }, ref) => {
    return (
      <Card 
        ref={ref} 
        className={className}
        {...props}
      >
        <div className="min-w-0 space-y-3">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <p className="min-w-0 flex-1 text-xs font-medium uppercase tracking-wider text-muted-foreground break-words">
              {title}
            </p>
            {icon && (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 p-3 text-primary">
                {icon}
              </div>
            )}
          </div>
          <div className="min-w-0 space-y-3">
            <p className="whitespace-nowrap text-2xl font-bold leading-tight tracking-tight xl:text-3xl">{value}</p>
            {change && (
              <div
                className={cn(
                  'inline-flex items-center gap-1.5 text-sm font-medium px-2 py-0.5 rounded-full',
                  change.trend === 'up' && 'text-emerald-600 bg-emerald-500/10',
                  change.trend === 'down' && 'text-red-600 bg-red-500/10',
                  change.trend === 'neutral' && 'text-muted-foreground bg-muted'
                )}
              >
                {change.trend === 'up' && (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                )}
                {change.trend === 'down' && (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                )}
                <span>{Math.abs(change.value)}%</span>
              </div>
            )}
            {description && (
              <p className="break-words text-sm text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
      </Card>
    );
  }
);

StatCard.displayName = 'StatCard';

// Card Title - for use within CardHeader with custom layouts
const CardTitle = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

// Card Description - for use within CardHeader with custom layouts
const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
);
CardDescription.displayName = 'CardDescription';

export { Card, CardHeader, CardContent, CardFooter, CardTitle, CardDescription, StatCard, cardVariants };
